/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@farmlands/shadcn-ui";
import { FC, JSX } from "react";

import TestIds from "../../../../../testIds";

export interface IDeleteAccountConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleteAccountClick: () => void;
  contactName?: string;
  accountEmail?: string;
}

const DeleteAccountConfirmation: FC<IDeleteAccountConfirmationProps> = ({
  open,
  onOpenChange,
  onDeleteAccountClick,
  contactName,
  accountEmail,
}): JSX.Element => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid={TestIds.feat.account.profile.deleteAccountModal.component}
      className="max-w-125!"
      onInteractOutside={(e) => e.preventDefault()}
    >
      <DialogHeader className="mb-6">
        <DialogTitle className="text-left">Delete your FarmlandsPRO account</DialogTitle>

        <DialogDescription className="sr-only">
          Confirm deletion of your FarmlandsPRO account
        </DialogDescription>
      </DialogHeader>

      <DialogBody
        data-testid={TestIds.feat.account.profile.deleteAccountModal.confirmation.component}
        className="text-2sx flex flex-col gap-6"
      >
        <p>
          Your FarmlandsPRO account will be removed, however you will still retain your shareholder
          account with Farmlands.
        </p>

        <div>
          {contactName && (
            <div
              className="font-semibold"
              data-testid={TestIds.feat.account.profile.deleteAccountModal.confirmation.contactName}
            >
              {contactName}
            </div>
          )}
          {accountEmail && (
            <div
              data-testid={
                TestIds.feat.account.profile.deleteAccountModal.confirmation.accountEmail
              }
            >
              {accountEmail}
            </div>
          )}
        </div>
      </DialogBody>

      <DialogFooter
        className={`flex flex-col-reverse justify-stretch gap-4 pt-6 md:flex-row md:justify-end`}
      >
        <Button
          size="lg"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          data-testid={TestIds.feat.account.profile.deleteAccountModal.confirmation.cancelBtn}
        >
          Cancel
        </Button>

        <Button
          size="lg"
          variant="destructive"
          onClick={onDeleteAccountClick}
          data-testid={
            TestIds.feat.account.profile.deleteAccountModal.confirmation.deleteAccountBtn
          }
        >
          Delete Account
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default DeleteAccountConfirmation;
